import {google} from 'google-maps';
import MapTypeId = google.maps.MapTypeId;
import MapOptions = google.maps.MapOptions;
import MapTypeStyle = google.maps.MapTypeStyle;

export class Map {
    private static getGoogleMapsStyle(): any[] {
        return [
            {
                elementType: 'labels.text.fill',
                featureType: 'all',
                stylers: [
                    {
                        saturation: 36,
                    },
                    {
                        color: '#656565',
                    },
                    {
                        lightness: 40,
                    },
                ],
            },
            {
                elementType: 'labels.text.stroke',
                featureType: 'all',
                stylers: [
                    {
                        visibility: 'on',
                    },
                    {
                        color: '#f7f7f7',
                    },
                    {
                        lightness: 16,
                    },
                ],
            },
            {
                elementType: 'labels.icon',
                featureType: 'all',
                stylers: [
                    {
                        visibility: 'off',
                    },
                ],
            },
            {
                elementType: 'geometry.fill',
                featureType: 'administrative',
                stylers: [
                    {
                        color: '#f7f7f7',
                    },
                    {
                        lightness: 20,
                    },
                ],
            },
            {
                elementType: 'geometry.stroke',
                featureType: 'administrative',
                stylers: [
                    {
                        color: '#fefefe',
                    },
                    {
                        lightness: 17,
                    },
                    {
                        weight: 1.2,
                    },
                ],
            },
            {
                elementType: 'labels.text.fill',
                featureType: 'administrative',
                stylers: [
                    {
                        color: '#585858',
                    },
                ],
            },
            {
                elementType: 'geometry',
                featureType: 'landscape',
                stylers: [
                    {
                        color: '#f7f7f7',
                    },
                    {
                        lightness: 20,
                    },
                ],
            },
            {
                elementType: 'geometry',
                featureType: 'poi',
                stylers: [
                    {
                        color: '#f7f7f7',
                    },
                    {
                        lightness: 21,
                    },
                ],
            },
            {
                elementType: 'geometry',
                featureType: 'poi.park',
                stylers: [
                    {
                        color: '#f7f7f7',
                    },
                    {
                        lightness: 21,
                    },
                ],
            },
            {
                elementType: 'geometry.fill',
                featureType: 'road.highway',
                stylers: [
                    {
                        color: '#c03535',
                    },
                    {
                        lightness: '3',
                    },
                    {
                        saturation: '-9',
                    },
                ],
            },
            {
                elementType: 'geometry',
                featureType: 'road.arterial',
                stylers: [
                    {
                        color: '#979797',
                    },
                    {
                        lightness: '18',
                    },
                    {
                        saturation: '-55',
                    },
                ],
            },
            {
                elementType: 'geometry.stroke',
                featureType: 'road.highway',
                stylers: [
                    {
                        color: '#f7f7f7',
                    },
                    {
                        lightness: 29,
                    },
                    {
                        weight: 0.2,
                    },
                ],
            },
            {
                elementType: 'geometry',
                featureType: 'road.local',
                stylers: [
                    {
                        color: '#dfdfdf',
                    },
                    {
                        lightness: 16,
                    },
                ],
            },
            {
                elementType: 'geometry',
                featureType: 'transit',
                stylers: [
                    {
                        color: '#ffffff',
                    },
                    {
                        lightness: 19,
                    },
                ],
            },
            {
                elementType: 'geometry',
                featureType: 'water',
                stylers: [
                    {
                        color: '#ebebeb',
                    },
                    {
                        lightness: 17,
                    },
                ],
            },
        ];
    }

    protected MAP_SELECTOR: string = '.map__placeholder';
    protected MAP_WRAPPER_SELECTOR: string = '.map__wrapper';
    protected LOCATION_CONTAINER_SELECTOR: string = '.map__location-container';
    protected LOCATION_SELECTOR: string = '.map__location';
    protected MOBILE_INDICATOR_SELECTOR: string = '.map__mobile-indicator';
    private mapTypeId: string = 'styled_map';
    private element: HTMLElement;
    private $element: JQuery;
    private $locations: JQuery;
    private $locationContainers: JQuery;
    private map: google.maps.Map;
    private infoWindow: google.maps.InfoWindow;
    private $mapContainer: JQuery;

    public constructor(element: HTMLElement) {
        this.element = element;
        this.$element = $(this.element);
        this.$element.data('self', this);
    }

    public init() {
        this.$mapContainer = this.$element.find(this.MAP_SELECTOR).first();
        this.$locations = this.$element.find(this.LOCATION_CONTAINER_SELECTOR);
        this.$locationContainers = this.$element.find(this.LOCATION_SELECTOR);
    }

    public onMapsScriptLoaded() {
        this.initializeMap();
    }

    public initializeMap() {
        let zoom = 11;
        let centerPos;

        if (this.isMobile()) {
            centerPos = new google.maps.LatLng(this.$mapContainer.data('xslat'), this.$mapContainer.data('xslong'));
            zoom = this.$mapContainer.data('xszoom');
        } else {
            centerPos = new google.maps.LatLng(this.$mapContainer.data('smlat'), this.$mapContainer.data('smlong'));
            zoom = this.$mapContainer.data('smzoom');
        }

        const mapOptions = {
            mapTypeControlOptions: {
                mapTypeIds: [this.mapTypeId],
            },
            mapTypeId: this.mapTypeId,
            scrollwheel: false,
            streetViewControl: false,
            zoom,
        };

        this.map = new google.maps.Map(this.$mapContainer[0], (mapOptions as any));
        this.infoWindow = new google.maps.InfoWindow();

        this.$locations.each((idx, location) => {
            this.addLocation($(location), idx);
        });

        this.map.setCenter(centerPos);

        const styledMap = new google.maps.StyledMapType(Map.getGoogleMapsStyle(), {});

        this.map.mapTypes.set(this.mapTypeId, styledMap);
    }

    private isMobile(): boolean {
        return $(this.MOBILE_INDICATOR_SELECTOR).is(':visible');
    }

    private addLocation(location: JQuery, idx: number) {
        const lat = location.data('lat');
        const long = location.data('long');
        const position = new google.maps.LatLng(lat, long);

        const icon = {
            url: '/images/map/marker.png',
            scaledSize: new google.maps.Size(48, 65), // scaled size
        };

        const marker = new google.maps.Marker({
            icon: icon,
            map: this.map,
            position,
            title: location.find('.location__title').text(),
            zIndex: idx,
        });

        const infoWindowContent = `
            <div class="map__marker-content">
                <div class="map__marker-text">
                    <div class="map__marker-headline">${ location.find('.map__location-headline').html() }</div>
                    <div class="map__marker-address">${ location.find('.map__location-address').html() }</div>
                    <div class="map__marker-place">${ location.find('.map__location-place').html() }</div>
                    <div class="map__marker-route">${ location.find('.map__location-route').html() }</div>
                </div>
            </div>
        `;

        const that = this;
        google.maps.event.addListener(marker, 'click', (() => {
            that.infoWindow.setContent(infoWindowContent);
            that.infoWindow.open(this.map, marker);
        }));

        google.maps.event.addListener(marker, 'mouseover', () => {
            const locationContainer: HTMLElement = that.$locationContainers.get(marker.getZIndex());
            $(locationContainer).addClass('map__location--active');
            $(that.MAP_WRAPPER_SELECTOR).addClass('map--active');
        });

        google.maps.event.addListener(marker, 'mouseout', () => {
            const locationContainer: HTMLElement = that.$locationContainers.get(marker.getZIndex());
            $(locationContainer).removeClass('map__location--active');
            $(that.MAP_WRAPPER_SELECTOR).removeClass('map--active');
        });
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./assets/components/map/map.class.ts